import serverVars from 'server-vars';
import { defineMessages, IntlShape } from 'dibs-react-intl';

export const mailMessages = defineMessages({
    subject: {
        id: 'abt.interiorsProfile.photo.share.email.subject',
        defaultMessage: 'Design Ideas for You on 1stDibs',
    },
    body: {
        id: 'abt.interiorsProfile.photo.share.email.body',
        defaultMessage: 'Check out {firmName}\'s "{projectName}" project on 1stDibs.',
    },
    pinterest: {
        id: 'abt.interiorsProfile.share.pinterest',
        defaultMessage: 'See more of {firmName}\'s "{projectName}" on 1stDibs',
    },
});

export const getShareUri: (projectUrl: string) => string = projectUrl =>
    `${serverVars.get('localeOrigin')}${projectUrl}`;

export const prepShareWindow: () => string = () => {
    const width = 665;
    const height = 500;

    const windowOptions = {
        scrollbars: 'yes',
        resizable: 'no',
        toolbar: 'no',
        height: height,
        width: width,
        top: 0,
        left: 0,
    };

    const screenHeight = screen.height;
    const screenWidth = screen.width;

    const halfScreenWidth = screenWidth / 2;
    const halfWidth = width / 2;
    windowOptions.left = Math.round(halfScreenWidth - halfWidth);

    if (screenHeight > height) {
        const halfScreenHeight = screenHeight / 2;
        const halfHeight = height / 2;

        windowOptions.top = Math.round(halfScreenHeight - halfHeight);
    }
    const windowString = (Object.keys(windowOptions) as (keyof typeof windowOptions)[])
        .reduce((prev, curr) => {
            prev.push(`${curr}=${windowOptions[curr]}`);
            return prev;
        }, [] as string[])
        .join(',');

    return windowString;
};

type CommonPinterestShare = {
    url: string;
    photoPath: string;
};

type ShareWithDescription = CommonPinterestShare & {
    description: string;
};

type ShareWithFirmAndProject = CommonPinterestShare & {
    firmName: string;
    projectName: string;
    intl: IntlShape;
};

type PinterestShare = ShareWithDescription | ShareWithFirmAndProject;

function isShareWithDescription(input: PinterestShare): input is ShareWithDescription {
    return (input as ShareWithDescription).description !== undefined;
}

export function shareViaPinterest(input: PinterestShare): void {
    const windowString = prepShareWindow();
    const pinWindow = window.open('', 'pinterest', windowString);

    let description = null;
    if (isShareWithDescription(input)) {
        description = input.description;
    } else {
        const { firmName, projectName } = input;
        description = input.intl.formatMessage(mailMessages.pinterest, {
            firmName,
            projectName,
        });
    }

    if (!description || !pinWindow) {
        return;
    }

    const searchParams = new URLSearchParams({
        url: getShareUri(input.url),
        media: input.photoPath,
        description,
    });

    pinWindow.location.href = `https://pinterest.com/pin/create/button/?${searchParams.toString()}`;
}

export function shareViaFacebook(photoImgPath: string | null): void {
    if (!window.FB || !window.FB.ui || !photoImgPath) {
        // eslint-disable-next-line no-console
        console.error('Could not load FB share');
        return;
    }

    window.FB.ui({
        //@ts-ignore TODO: ENGAGE-1738
        method: 'stream.share',
        href: window.location.href,
        image: photoImgPath,
    });
}

type EmailShare = {
    subject: string;
    body: string;
    url: string;
};

export function shareViaEmail({ subject, body, url }: EmailShare): void {
    const subjectLine = encodeURIComponent(subject);
    const bodyText = `${encodeURIComponent(body)}%0D%0A%0D%0A${url}`;

    window.open(`mailto:?subject=${subjectLine}&body=${bodyText}`, '_blank');
}
